/** The semantic regions every Guided interview surface must account for. */
export const GUIDED_INTERVIEW_SEMANTIC_SLOTS = [
  'prompt',
  'reveal',
  'choices',
  'freeform',
  'progress',
  'validation',
  'terminal',
  'dismissal',
] as const;

export type GuidedInterviewSemanticSlot = (typeof GUIDED_INTERVIEW_SEMANTIC_SLOTS)[number];

export type GuidedInterviewSurface = 'chat' | 'desktop' | 'phone' | 'tv' | 'legacy' | 'snapshot';

/**
 * `not-applicable` is intentional for display-only surfaces. Keeping that
 * state in the contract prevents a layout adapter from silently dropping a
 * driver-only affordance while preserving the adapter's non-interactive DOM.
 */
export type GuidedInterviewSemanticSlotState = 'present' | 'empty' | 'not-applicable';

export type GuidedInterviewSemanticSlotStates = Readonly<
  Record<GuidedInterviewSemanticSlot, GuidedInterviewSemanticSlotState>
>;

export interface GuidedInterviewSemanticCompositionProps {
  surface: GuidedInterviewSurface;
  slotStates: GuidedInterviewSemanticSlotStates;
}

/**
 * Shared semantic boundary for the Guided interview's surface adapters.
 *
 * The marker is `display: contents` so desktop, phone, TV, legacy, and
 * snapshot layouts remain owned by their existing adapters. It is an inline
 * neutral element rather than a `div` so it cannot change existing direct-div
 * layout/test seams. The hidden markers are intentionally executable DOM state:
 * component tests can prove that a surface accounted for every semantic region
 * without coupling to CSS or source-text branches.
 */
export function GuidedInterviewSemanticComposition({
  surface,
  slotStates,
}: GuidedInterviewSemanticCompositionProps): React.JSX.Element {
  return (
    <span
      className="contents"
      data-testid="guided-interview-semantic-composition"
      data-guided-interview-surface={surface}
      data-guided-interview-slot-contract={GUIDED_INTERVIEW_SEMANTIC_SLOTS.join(' ')}
    >
      {GUIDED_INTERVIEW_SEMANTIC_SLOTS.map((slot) => (
        <span
          key={slot}
          aria-hidden="true"
          className="hidden"
          data-guided-interview-slot={slot}
          data-guided-interview-slot-state={slotStates[slot]}
        />
      ))}
    </span>
  );
}
